import React, { useCallback, useEffect, useRef, useState } from "react";
import "./Header.scss";
import {
  IoBookOutline,
  IoSearchOutline,
  IoCloseCircleOutline,
  IoExitOutline,
  IoPersonSharp
} from "react-icons/io5";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Songbook } from "../../../@core/models";
import { Auth } from "../../../@core/auth";
import debounce from "lodash/debounce";
import RestClient from "../../../@core/restClient";
import Spinner from "../../widgets/UI/Spinner";

type queryState = [query: string, setQuery: (query: string) => void];

type SongbooksState = [
  songbooks: Songbook[],
  setSongbooks: (songbooks: Songbook[]) => void
];

type IsOpenState = [isOpen: boolean, setIsOpen: (isOpen: boolean) => void];

const Header = () => {
  const queryRef = useRef<any>();
  const [query, setQuery]: queryState = useState<string>("");
  const [songList, setSongList] = useState<any[]>([]);
  const [isOpen, setIsOpen]: IsOpenState = useState(false);
  const [categoryPrompt, setCategoryPrompt] = useState(false);
  const [chooseCategory, setChoseCategory] = useState(false);
  const [category, setCategory] = useState("");
  const [categoryList, setCategoryList] = useState<any>([]);
  const [categoryName, setCategoryName] = useState("");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isLibrary = pathname.includes("/library");
  const [person, setPerson] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchInText, setSearchInText] = useState(false);
  const [songbooks, setSongbooks] = useState<Songbook[]>([]);
  const [filteredSongbooks, setFilteredSongbooks] = useState<Songbook[]>([]);
  const [bookDropOpen, setBookDropOpen] = useState(false);
  const [bookFilter, setBookFilter] = useState("");
  const bookFilterRef = useRef<any>();

  // CHEKING AUTHENTICATION ON LOAD AND ON VISIBILITY CHANGE
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        if (Auth.isAuthenticated) {
          setPerson(true);
        } else {
          setPerson(false);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // CHECK AUTHENTICATION ON MOUNT
  useEffect(
    function () {
      if (Auth.isAuthenticated) {
        setPerson(true);
      } else {
        setPerson(false);
      }
    },
    [localStorage.getItem("User")]
  );
  //CHEKING AUTHENTICATION EVERY 30 MIN

  useEffect(() => {
    const interval = setInterval(
      () => {
        if (Auth.isAuthenticated) {
          setPerson(true);
        } else {
          setPerson(false);
        }
      },
      30 * 60 * 1000
    );
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!isLibrary) return;
    (async () => {
      try {
        const response = await RestClient.getSongbookList("");
        const sorted = (response || []).sort((a: Songbook, b: Songbook) =>
          a.title.localeCompare(b.title, "uk")
        );
        setSongbooks(sorted);
        setFilteredSongbooks(sorted);
      } catch (error) {
        console.log(error);
      }
    })();
  }, [isLibrary]);

  useEffect(() => {
    if (!bookFilter) {
      setFilteredSongbooks(songbooks);
      return;
    }
    setFilteredSongbooks(
      songbooks.filter((sb) =>
        sb.title.toLowerCase().includes(bookFilter.toLowerCase())
      )
    );
  }, [bookFilter, songbooks]);

  useEffect(() => {
    if (!bookDropOpen) return;
    const handleClick = () => {
      setBookDropOpen(false);
      setBookFilter("");
      if (bookFilterRef.current) bookFilterRef.current.value = "";
    };
    document.body.addEventListener("click", handleClick);
    return () => document.body.removeEventListener("click", handleClick);
  }, [bookDropOpen]);

  const goHome = () => {
    setIsOpen(false);
    navigate("/");
  };

  const deferredSetQueryHandler = useCallback(
    debounce((event: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(event.target.value);
    }, 500),
    []
  );
  useEffect(() => {
    (async () => {
      try {
        const response = await RestClient.getCategoryList();
        setCategoryList(response || []);
      } catch (error) {
        console.log(error);
      }
    })();
  }, []);

  useEffect(
    function () {
      (async () => {
        if (!query && !category) {
          setIsOpen(false);
          return;
        }
        try {
          setIsLoading(true);
          const response = await RestClient.getSongListByQuery(
            query,
            category,
            true,
            undefined,
            undefined,
            searchInText ? "text" : undefined
          );
          setSongList(response || []);
          setIsOpen(true);
          setChoseCategory(false);
        } catch (error) {
          console.log(error);
        }

        setIsLoading(false);
      })();
    },
    [query, category, searchInText]
  );

  const selectSongbook = (songbookId: any, songId: any) => {
    setSongList([]);
    queryRef.current.value = "";
    setQuery("");
    navigate(`/songbook/${songbookId}/song/${songId}`);
    setIsOpen(false);
    setCategoryPrompt(false);
    setChoseCategory(false);
    setCategory("");
    setCategoryName("");
  };

  const goToCabinet = () => {
    navigate("/cabinet");
  };

  useEffect(() => {
    if (isOpen || categoryPrompt || chooseCategory) {
      // Named function so we can reference it for removal
      const handleClick = () => {
        setIsOpen(false);
        setCategoryPrompt(false);
        setChoseCategory(false);
        setQuery("");
        setCategory("");
        queryRef.current.value = "";
        setSongList([]);
        setCategoryName("");
      };

      document.body.addEventListener("click", handleClick);
      // Cleanup function - runs when effect re-runs or component unmounts
      return () => {
        document.body.removeEventListener("click", handleClick);
      };
    }
  }, [isOpen, categoryPrompt, chooseCategory]);

  const cancelCategories = (e: any) => {
    e.stopPropagation();
    setCategoryName("");
    setCategory("");
    if (queryRef.current.value !== "") {
      setQuery(queryRef.current.value);
    } else {
      setQuery("");
      setIsOpen(false);
      setChoseCategory(true);
    }
  };

  return (
    <header className="row">
      <div className="col-6 col-xl-3 mb-3">
        <div className="d-flex align-items-center">
          <a onClick={goHome}>
            <img
              src="/images/header-logo.png"
              className="img-logo"
              alt="logo"
            />
          </a>
          <div className="text-logo">
            <p className="title text-uppercase">Aryn-lyrics</p>
            <button className="sub-title" onClick={goToCabinet}>
              Створити пісенник
            </button>
          </div>
        </div>
      </div>

      <div className="col-12 col-xl-6 order-3 order-xl-2 mb-3">
        <div className="h-100 d-flex align-items-center">
          <div className="songbook-input-container">
            {isLibrary ? (
              <div onClick={(e) => e.stopPropagation()}>
                <button
                  className="btn-mcolor"
                  style={{ display: "block", margin: "0 auto" }}
                  onClick={() => setBookDropOpen((prev) => !prev)}
                >
                  Перейти до пісенників
                </button>
                {bookDropOpen && (
                  <div className="select-container">
                    <IoCloseCircleOutline
                      className="close"
                      onClick={() => {
                        setBookDropOpen(false);
                        setBookFilter("");
                        if (bookFilterRef.current)
                          bookFilterRef.current.value = "";
                      }}
                    />
                    <div className="library-book-search">
                      <IoSearchOutline className="library-book-search-icon" />
                      <input
                        ref={bookFilterRef}
                        type="text"
                        placeholder="Знайти пісенник..."
                        className="library-book-search-input"
                        autoFocus
                        onChange={(e) => setBookFilter(e.target.value)}
                      />
                    </div>
                    {filteredSongbooks.map((sb: Songbook) => (
                      <div
                        key={sb.id}
                        className="select-item"
                        onClick={() => {
                          setBookDropOpen(false);
                          setBookFilter("");
                          navigate(`/songbook/${sb.id}`);
                        }}
                      >
                        <p>{sb.title}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="songbook-input-container-spinner">
                  {isLoading ? <Spinner /> : ""}
                </div>
                <input
                  type="text"
                  ref={queryRef}
                  className="songbook-input"
                  placeholder="Знайти пісню на сайті. . ."
                  onChange={deferredSetQueryHandler}
                  onClick={(e) => {
                    setCategoryPrompt(true);
                    e.stopPropagation();
                  }}
                />
                <IoSearchOutline className="search" />
                {categoryPrompt ? (
                  <div
                    className="category-prompt"
                    onClick={(e) => {
                      setChoseCategory(true);
                      e.stopPropagation();
                    }}
                  >
                    <label
                      className="search-in-text"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        className="search-in-text-input"
                        checked={searchInText}
                        onChange={() => setSearchInText((v) => !v)}
                      />
                      <span className="search-in-text-box" />
                      Шукати у тексті
                    </label>
                    <p className="btn-mcolor category-prompt-btn">
                      Шукати за катерогіями
                    </p>
                  </div>
                ) : null}
                {chooseCategory ? (
                  <div
                    className="choose-category"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IoCloseCircleOutline
                      className="close"
                      onClick={() => {
                        setChoseCategory(false);
                      }}
                    />
                    {[...(categoryList ?? [])].sort((a: any, b: any) => a.name.localeCompare(b.name, "uk")).map((item: any) => (
                      <p
                        key={item.id}
                        onClick={() => {
                          setCategory(item.id.toString());
                          setCategoryName(item.name);
                        }}
                      >
                        {item.name}
                      </p>
                    ))}
                  </div>
                ) : null}

                {songList?.length > 0 && isOpen && (
                  <div className="select-container">
                    <IoCloseCircleOutline
                      className="close"
                      onClick={() => {
                        setIsOpen(false);
                        setCategoryPrompt(false);
                        setChoseCategory(false);
                        setQuery("");
                        setCategory("");
                        queryRef.current.value = "";
                        setSongList([]);
                        setCategoryName("");
                      }}
                    />
                    {categoryName ? (
                      <div>
                        <p
                          className="select-container-cancel_categories"
                          onClick={(e) => cancelCategories(e)}
                        >
                          Скинути категорії
                        </p>
                        <p className="select-container-category_name">
                          {categoryName}
                        </p>
                      </div>
                    ) : null}
                    {songList?.map((song: any, index: number) => (
                      <div
                        className="select-item"
                        key={index}
                        onClick={() => selectSongbook(song.songbookId, song.id)}
                      >
                        <p>{song.title}</p>{" "}
                        <p className="select-item-songbook">
                          &nbsp;&nbsp;&nbsp;
                          {song.songbook.title}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <div className="col-6 col-xl-3 order-2 order-xl-3 mb-3">
        <div className="h-100 d-flex justify-content-end align-items-center">
          <div className="manual-tooltip">
            <IoBookOutline
              className="manual-icon"
              onClick={() => navigate("/manual")}
            />
            <span className="manual-tooltip-text">Довідник</span>
          </div>
          {!person ? (
            <button
              className="signIn btn-plain"
              onClick={() => navigate("/loginmodal")}
            >
              Увійти
            </button>
          ) : (
            <div className="logedIn">
              <div className="login-box">
                <div className="login-box-tooltip">
                  <button className="login" onClick={goToCabinet}>
                    <IoPersonSharp className="login-icon" />
                  </button>
                  <span className="login-box-tooltip-text">Кабінет</span>
                </div>
                <div
                  className="user-email"
                  onClick={() => navigate("/loginmodal")}
                >
                  <div>
                    {
                      JSON.parse(localStorage.getItem("User") || "{}")
                        ?.firstName
                    }
                  </div>
                  <div>
                    {JSON.parse(localStorage.getItem("User") || "{}")?.lastName}
                  </div>
                </div>
              </div>
              <div className="exit-tooltip">
                <IoExitOutline
                  className="exit"
                  onClick={() => {
                    localStorage.removeItem("User");
                    localStorage.removeItem("Token");
                    setPerson(false);
                    goHome();
                  }}
                />
                <span className="exit-tooltip-text">Вийти</span>
              </div>
            </div>
          )}
        </div>
        {/* {loginModalShow ?
          <div>
            <LoginModal closeModal={closeLoginModal} />
          </div> : null
        } */}
      </div>
      {bookDropOpen && (
        <div
          className="book-drop-backdrop"
          onClick={() => {
            setBookDropOpen(false);
            setBookFilter("");
            if (bookFilterRef.current) bookFilterRef.current.value = "";
          }}
        />
      )}
    </header>
  );
};

export default Header;
